import { useTranslation } from "react-i18next";

import { formatDateRelative } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import type { MessageThreadWithParticipants } from "simplydo/interfaces/types/messageTypes";
import { H5 } from "simplydo/react/ui/header";
import { Label } from "simplydo/react/ui/label";
import { MenuItem } from "simplydo/react/ui/menu";

import { useAppSelector } from "store";
import util from "utils/utils";

import { EmptyBox } from "components/lib/UI";

import { UserChip } from "../Chips";

type MessageTrayMessagesProps = {
  messageThreads: MessageThreadWithParticipants[];
  selectMessageThread: (thread: OpenAPI.Schemas["MessageThread"]) => void;
};

const MessageTrayMessages = ({
  messageThreads = [],
  selectMessageThread,
}: MessageTrayMessagesProps) => {
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();

  if (!messageThreads.length) {
    return (
      <MenuItem>
        <EmptyBox>
          <H5>{t("messages.empty")}</H5>
        </EmptyBox>
      </MenuItem>
    );
  }
  const numberOfMessagesToShow = window?.innerHeight
    ? (0.6 * window?.innerHeight - 100) / 85
    : 6;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {messageThreads.slice(0, numberOfMessagesToShow).map((thread) => {
        if (thread.lastMessageAt || thread.participants.length > 2) {
          const hasMoreThanTwoParticipants =
            thread?.participantUsers?.length &&
            thread?.participantUsers?.length > 2;
          const otherUsers = (thread.participantUsers ?? []).filter(
            (p) => p._id !== user?._id,
          );

          return (
            thread.participantUsers &&
            thread.participantUsers.length > 0 && (
              <MenuItem
                key={thread._id}
                onClick={() => selectMessageThread(thread)}
                style={{
                  padding: ".433em",
                  flexDirection: "column",
                  borderRadius: 6,
                  marginTop: 0,
                  marginBottom: 0,
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    marginBottom: 10,
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      maxWidth: "75%",
                    }}
                  >
                    {hasMoreThanTwoParticipants ? (
                      <span
                        style={{
                          fontWeight: "bold",
                          fontSize: 15,
                          display: "block",
                          margin: 0,
                        }}
                      >
                        {util.getChatName(user, thread)}
                      </span>
                    ) : (
                      <UserChip user={otherUsers[0]} compact />
                    )}
                  </div>
                  {thread.lastMessageSnippet && thread.lastMessageAt && (
                    <span>
                      {formatDateRelative(thread.lastMessageAt, {
                        addSuffix: true,
                      })}
                    </span>
                  )}
                </div>
                {thread.seenBy &&
                  user?._id &&
                  thread.seenBy.indexOf(user._id) === -1 && (
                    <Label size="small" color="teal">
                      New
                    </Label>
                  )}
                {thread.lastMessageSnippet || thread.lastMessageAt ? (
                  <span>
                    {thread.lastMessageUser === user?._id && "You: "}{" "}
                    {thread.lastMessageSnippet ||
                      formatDateRelative(thread.lastMessageAt, {
                        addSuffix: true,
                      })}
                  </span>
                ) : null}
              </MenuItem>
            )
          );
        }
        return null;
      })}
    </div>
  );
};

export default MessageTrayMessages;
